import { Link } from 'react-router-dom';
import SEO from '../../components/SEO';
import Sources from '../../components/Sources';
import FAQSection from '../../components/FAQSection';
import TableOfContents from '../../components/TableOfContents';
import Callout from '../../components/Callout';
import Breadcrumbs from '../../components/Breadcrumbs';

export default function TrailerNummerplade() {
  const breadcrumbs = [
    { name: "Forside", path: "/" },
    { name: "Syn og udstyr", path: "/trailer-syn" },
    { name: "Nummerplade på trailer", path: "/trailer-nummerplade" }
  ];

  const tocItems = [
    { id: "regler-for-trailerplade", title: "Regler for nummerplade på trailer" },
    { id: "priser-og-gebyrer", title: "Pris på nummerplade og omregistrering" },
    { id: "bestilling-og-afhentning", title: "Hvor og hvordan køber man pladen?" },
    { id: "montering-og-tyverisikring", title: "Regler for montering og fastgørelse" },
    { id: "nummerpladelygte", title: "Lovkrav til nummerpladelygte og læsbarhed" },
    { id: "eu-maerke-og-udland", title: "EU-symbol kontra ovalt DK-nationalitetsmærke" },
    { id: "afmelding-og-bortkomst", title: "Afmelding og mistet nummerplade" }
  ];

  const faq = [
    {
      q: "Hvor meget koster en ny nummerplade til trailer?",
      a: "En enkelt nummerplade til en trailer koster 590 kr. i fast statsligt gebyr hos Motorstyrelsen. Køber du pladen hos en privat nummerpladeoperatør (f.eks. en synshal eller trailerforhandler), opkræver operatøren typisk et mindre ekspeditionsgebyr oveni."
    },
    {
      q: "Hvor mange nummerplader skal der sidde på en trailer?",
      a: "En trailer skal udelukkende have én nummerplade, som skal være monteret bagpå i midten eller i venstre side, så den er tydeligt synlig og læsbar bagfra."
    },
    {
      q: "Skal en nummerplade på en trailer skrues fast med hætter?",
      a: "Nej. Lovkravet om tyverisikring med fastskruning og farvede dækhætter gælder udelukkende person- og varebiler. På en trailer skal pladen blot være solidt og forsvarligt fastgjort – f.eks. i en godkendt nummerpladeramme eller fastboltet med skruer eller nitter."
    },
    {
      q: "Hvad koster det at omregistrere en trailer ved ejerskifte?",
      a: "Omregistrering (ejerskifte) koster 340 kr., hvis du gør det selv direkte i Motorregistret på skat.dk med MitID. Gør du det hos en nummerpladeoperatør, koster det 380 kr. i statsligt gebyr plus operatørens eget ekspeditionsgebyr."
    },
    {
      q: "Hvor lang frist har køber til at omregistrere en trailer?",
      a: "Køber har ifølge loven 4 hverdage til at omregistrere traileren til sit eget navn i Motorregistret. Det sikrer, at tidligere ejer ikke modtager eventuelle p-afgifter eller vægtafgift."
    }
  ];

  const sources = [
    { title: "Motorstyrelsen: Regler for trailer og påhængsvogn", source: "motorst.dk", url: "https://motorst.dk/borger/nummerplader/trailer" },
    { title: "Motorstyrelsen: Bestilling og priser på nummerplader", source: "motorst.dk", url: "https://motorst.dk/borger/nummerplader/bestilling-af-nummerplader" },
    { title: "Bekendtgørelse om registrering af køretøjer (BEK nr. 1498 af 30/06/2021)", source: "retsinformation.dk", url: "https://www.retsinformation.dk/eli/lta/2021/1498" }
  ];

  return (
    <>
      <SEO 
        title="Trailer nummerplade: Pris, regler, montering og lys" 
        description="Få overblik over regler for nummerplader til trailer: pris (590 kr.), omregistrering (340 kr.), EU-mærke, nummerpladelygte og lovlig montering." 
        pathname="/trailer-nummerplade"
        faq={faq}
        breadcrumbs={breadcrumbs}
      />

      <article>
        <Breadcrumbs items={breadcrumbs} />

        <h1 className="text-[30px] sm:text-[36px] font-bold text-[#2E3A46] tracking-tight leading-tight mb-4">
          Nummerplade på trailer: Regler, pris og montering
        </h1>

        <p className="text-[18px] text-[#1A1C1F] leading-relaxed mb-6 font-medium">
          I Danmark skal en registreringspligtig trailer altid bære <strong>én nummerplade monteret bagpå</strong>. En ny nummerplade koster <strong>590 kr.</strong> i fast statsligt gebyr hos Motorstyrelsen.
        </p>

        <p className="text-[17px] text-[#1A1C1F] leading-relaxed mb-6">
          Køber du en brugt trailer med eksisterende nummerplade, koster omregistrering 340 kr. online i Motorregistret. Nummerpladen skal være oplyst af mindst én hvid lygte under kørsel i lygtetændingstiden og skal være monteret plant og vinkelret bagpå traileren, så den kan aflæses ubesværet.
        </p>

        <TableOfContents items={tocItems} />

        <Callout title="Nøgletal for trailernummerplader">
          <ul className="m-0 p-0 list-none space-y-1 font-mono text-[15px]">
            <li>• Pris for ny nummerplade: <strong>590 kr.</strong></li>
            <li>• Omregistrering (selvbetjening i DMR): <strong>340 kr.</strong></li>
            <li>• Omregistrering hos operatør: <strong>380 kr. + gebyr</strong></li>
            <li>• Antal nummerplader: <strong>1 stk. bagpå</strong></li>
            <li>• Frist for omregistrering ved køb: <strong>4 hverdage</strong></li>
          </ul>
        </Callout>

        <h2 id="regler-for-trailerplade" className="text-[22px] font-bold text-[#2E3A46] mt-10 mb-4">
          Regler for nummerplade på trailer
        </h2>

        <p className="text-[17px] text-[#1A1C1F] leading-relaxed mb-4">
          Enhver påhængsvogn til person- eller varebil, der anvendes på færdselslovens område i Danmark, skal være optaget i Motorregistret (DMR) og forsynet med en officiel nummerplade. De eneste undtagelser er uregistrerede landbrugsredskaber og særlige efterløbsredskaber.
        </p>

        <p className="text-[17px] text-[#1A1C1F] leading-relaxed mb-6">
          Nummerpladen identificerer køretøjet over for politiet og færdselsmyndighederne. Det er altid den registrerede ejers og førerens fælles ansvar, at pladen er ren, ubeskadiget, plan og monteret på en sådan måde, at den ikke kan forveksles eller aflæses forkert.
        </p>

        <h2 id="priser-og-gebyrer" className="text-[22px] font-bold text-[#2E3A46] mt-10 mb-4">
          Pris på nummerplade og omregistrering
        </h2>

        <p className="text-[17px] text-[#1A1C1F] leading-relaxed mb-4">
          Motorstyrelsen fastsætter de officielle takster for nummerplader og registrering i henhold til registreringsbekendtgørelsen:
        </p>

        <div className="my-6 overflow-x-auto border-t border-[#B58200]">
          <table className="w-full text-left text-[15px] border-collapse">
            <thead>
              <tr className="border-b border-[#E4E1D8] font-mono text-[13px] uppercase text-[#2E3A46]">
                <th className="py-2.5 pr-4 font-semibold">Ydelse</th>
                <th className="py-2.5 pr-4 font-semibold">Sted</th>
                <th className="py-2.5 pr-4 font-semibold">Statsligt gebyr</th>
                <th className="py-2.5 font-semibold">Eventuelt operatørgebyr</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#E4E1D8] tabular-nums">
              <tr>
                <td className="py-3 pr-4 font-bold text-[#2E3A46]">Ny nummerplade (1 stk.)</td>
                <td className="py-3 pr-4">Synshal / forhandler</td>
                <td className="py-3 pr-4">590 kr.</td>
                <td className="py-3 text-[#5C636B]">Typisk 50 – 150 kr.</td>
              </tr>
              <tr>
                <td className="py-3 pr-4 font-bold text-[#2E3A46]">Omregistrering (online)</td>
                <td className="py-3 pr-4">Skat.dk (MitID)</td>
                <td className="py-3 pr-4">340 kr.</td>
                <td className="py-3 text-[#5C636B]">Intet (0 kr.)</td>
              </tr>
              <tr>
                <td className="py-3 pr-4 font-bold text-[#2E3A46]">Omregistrering (fysisk)</td>
                <td className="py-3 pr-4">Nummerpladeoperatør</td>
                <td className="py-3 pr-4">380 kr.</td>
                <td className="py-3 text-[#5C636B]">Fastsættes af operatøren</td>
              </tr>
              <tr>
                <td className="py-3 pr-4 font-bold text-[#2E3A46]">Erstatningsnummerplade</td>
                <td className="py-3 pr-4">Motorstyrelsen</td>
                <td className="py-3 pr-4">590 kr.</td>
                <td className="py-3 text-[#5C636B]">Forsendelse / ekspedition</td>
              </tr>
              <tr>
                <td className="py-3 pr-4 font-bold text-[#2E3A46]">Afmelding af trailer</td>
                <td className="py-3 pr-4">Nummerpladeoperatør</td>
                <td className="py-3 pr-4">0 kr.</td>
                <td className="py-3 text-[#5C636B]">Evt. lille gebyr hos operatør</td>
              </tr>
            </tbody>
          </table>
        </div>

        <h2 id="bestilling-og-afhentning" className="text-[22px] font-bold text-[#2E3A46] mt-10 mb-4">
          Hvor og hvordan køber man pladen?
        </h2>

        <p className="text-[17px] text-[#1A1C1F] leading-relaxed mb-4">
          Når du køber en fabriksny trailer hos en forhandler, er forhandleren i langt de fleste tilfælde autoriseret nummerpladeoperatør. Det betyder, at forhandleren kan indregistrere traileren i Motorregistret og montere nummerpladen direkte ved udlevering.
        </p>

        <p className="text-[17px] text-[#1A1C1F] leading-relaxed mb-6">
          Køber du en uindregistreret trailer eller henter en importeret påhængsvogn, kan du købe nummerpladen hos en godkendt synshal eller i et af Motorstyrelsens fire ekspeditionscentre (Aalborg, Aarhus, Odense eller Høje Taastrup). Du skal medbringe trailerens originale dataerklæring eller registreringsattest samt billedlegitimation.
        </p>

        <h2 id="montering-og-tyverisikring" className="text-[22px] font-bold text-[#2E3A46] mt-10 mb-4">
          Regler for montering og fastgørelse
        </h2>

        <p className="text-[17px] text-[#1A1C1F] leading-relaxed mb-4">
          Mange bilejere er bekendt med, at bilens nummerplader ved lov skal skrues fast med mindst to skruer eller bolte, som er dækket af hætter i samme farve som pladens baggrund.
        </p>

        <p className="text-[17px] text-[#1A1C1F] leading-relaxed mb-4">
          For påhængsvogne gælder der imidlertid en lempeligere regel i henhold til registreringsbekendtgørelsen:
        </p>

        <ul className="list-disc list-inside space-y-2 mb-6 text-[#1A1C1F]">
          <li>Kravet om fastskruning med farvede hætter <strong>gælder ikke for trailere</strong>.</li>
          <li>Nummerpladen må gerne monteres i en almindelig lukket nummerpladeramme (f.eks. af plast med klikliste).</li>
          <li>Pladen skal dog være <strong>solidt og forsvarligt fastgjort</strong>, så den ikke kan falde af under kørslen eller vibrere løs.</li>
          <li>Pladen skal placeres bagpå påhængsvognen, enten centreret eller i venstre side i kørselsretningen. Den må aldrig sidde i højre side alene.</li>
        </ul>

        <p className="text-[17px] text-[#1A1C1F] leading-relaxed mb-6">
          Selvom loven tillader en simpel klikramme, anbefaler mange færdselskyndige alligevel at sikre pladen med to nitter eller bolte. Påhængsvogne er ofte udsat for kraftige vibrationer, og tyveri af trailernummerplader til brug på ulovlige køretøjer forekommer jævnligt.
        </p>

        <h2 id="nummerpladelygte" className="text-[22px] font-bold text-[#2E3A46] mt-10 mb-4">
          Lovkrav til nummerpladelygte og læsbarhed
        </h2>

        <p className="text-[17px] text-[#1A1C1F] leading-relaxed mb-4">
          Ifølge bekendtgørelse om køretøjers indretning og udstyr skal trailerens nummerplade være forsynet med mindst én hvid nummerpladelygte:
        </p>

        <ul className="list-disc list-inside space-y-2 mb-6 text-[#1A1C1F]">
          <li><strong>Belysning:</strong> Lygten skal tænde automatisk sammen med bilens baglygter og oplyse hele nummerpladens felt med et jævnt hvidt lys.</li>
          <li><strong>Aflæselighed:</strong> Pladens tal og bogstaver skal i mørke og klart vejr være let læselige i en afstand af mindst 20 meter bagfra.</li>
          <li><strong>Intet hvidt lys bagud:</strong> Lygtehuset skal være afskærmet, så der ikke kastes direkte hvidt lys bagud mod efterfølgende trafikanter.</li>
        </ul>

        <p className="text-[17px] text-[#1A1C1F] leading-relaxed mb-6">
          En defekt pære eller løs forbindelse i nummerpladelygten er en af de mest almindelige årsager til bøder ved politiets vejsidekontroller og betinget godkendelse ved syn. Se de fulde krav til bagbelysning under <Link to="/trailer-lys-regler" className="text-[#2E3A46] font-medium hover:text-[#B58200] hover:underline underline-offset-2">trailer lys regler</Link>.
        </p>

        <h2 id="eu-maerke-og-udland" className="text-[22px] font-bold text-[#2E3A46] mt-10 mb-4">
          EU-symbol kontra ovalt DK-nationalitetsmærke
        </h2>

        <p className="text-[17px] text-[#1A1C1F] leading-relaxed mb-4">
          Siden 2009 har danske køretøjsejere haft frit valg mellem nummerplader med eller uden det blå EU-symbol i venstre side (med Danmarks nationalitetsbetegnelse „DK“ og de 12 EU-stjerner).
        </p>

        <p className="text-[17px] text-[#1A1C1F] leading-relaxed mb-4">
          Hvis du påtænker at køre med traileren i udlandet, har valget af nummerplade direkte juridisk betydning:
        </p>

        <div className="my-6 overflow-x-auto border-t border-[#B58200]">
          <table className="w-full text-left text-[15px] border-collapse">
            <thead>
              <tr className="border-b border-[#E4E1D8] font-mono text-[13px] uppercase text-[#2E3A46]">
                <th className="py-2.5 pr-4 font-semibold">Nummerpladetype</th>
                <th className="py-2.5 pr-4 font-semibold">Kørsel i Danmark</th>
                <th className="py-2.5 pr-4 font-semibold">Kørsel i EU/EØS</th>
                <th className="py-2.5 font-semibold">Krav om ekstra DK-mærke</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#E4E1D8] tabular-nums">
              <tr>
                <td className="py-3 pr-4 font-bold text-[#2E3A46]">Nummerplade med EU-mærke</td>
                <td className="py-3 pr-4">Lovlig</td>
                <td className="py-3 pr-4">Lovlig i alle EU-lande</td>
                <td className="py-3 text-[#5C636B]">Intet ekstra mærke krævet</td>
              </tr>
              <tr>
                <td className="py-3 pr-4 font-bold text-[#2E3A46]">Nummerplade uden EU-mærke</td>
                <td className="py-3 pr-4">Lovlig</td>
                <td className="py-3 pr-4">Lovlig med nationalitetsmærke</td>
                <td className="py-3 font-semibold text-[#2E3A46]">Kræver ovalt hvidt DK-klistermærke</td>
              </tr>
            </tbody>
          </table>
        </div>

        <p className="text-[17px] text-[#1A1C1F] leading-relaxed mb-6">
          Kører du til f.eks. Tyskland eller Sverige med en trailer uden EU-nummerplade, risikerer du bøder, medmindre der er monteret et godkendt internationalt ovalt DK-skilt på trailerens bagende. Læs mere om udenlandskørsel under <Link to="/trailer-i-tyskland" className="text-[#2E3A46] font-medium hover:text-[#B58200] hover:underline underline-offset-2">trailer i Tyskland</Link> og <Link to="/trailer-i-sverige" className="text-[#2E3A46] font-medium hover:text-[#B58200] hover:underline underline-offset-2">trailer i Sverige</Link>.
        </p>

        <h2 id="afmelding-og-bortkomst" className="text-[22px] font-bold text-[#2E3A46] mt-10 mb-4">
          Afmelding og mistet nummerplade
        </h2>

        <p className="text-[17px] text-[#1A1C1F] leading-relaxed mb-4">
          Når en trailer ikke længere skal benyttes, eller hvis den skrottes eller sælges til udlandet, skal den afmeldes i Motorregistret:
        </p>

        <ul className="list-disc list-inside space-y-2 mb-6 text-[#1A1C1F]">
          <li><strong>Aflevering hos operatør:</strong> En trailer kan ikke afmeldes digitalt. Du skal fysisk indlevere nummerpladen hos en autoriseret nummerpladeoperatør eller et af Motorstyrelsens ekspeditionscentre.</li>
          <li><strong>Mistet nummerplade:</strong> Hvis nummerpladen tabes eller stjæles, skal tabet anmeldes i Motorregistret. Da en trailer kun har én plade, kan man ikke køre videre. Du skal bestille en ny nummerplade til 590 kr., før traileren lovligt må anvendes på offentlig vej igen.</li>
        </ul>

        <p className="text-[17px] text-[#1A1C1F] leading-relaxed mb-6">
          Se også vores oversigter over <Link to="/trailer-syn" className="text-[#2E3A46] font-medium hover:text-[#B58200] hover:underline underline-offset-2">trailer syn</Link>, <Link to="/trailer-vaegtafgift" className="text-[#2E3A46] font-medium hover:text-[#B58200] hover:underline underline-offset-2">trailer vægtafgift</Link> og <Link to="/trailer-forsikring" className="text-[#2E3A46] font-medium hover:text-[#B58200] hover:underline underline-offset-2">trailerforsikring</Link>.
        </p>

        <FAQSection items={faq} />
        <Sources links={sources} />
      </article>
    </>
  );
}
